<script setup lang="ts">
import { ref, onMounted } from "vue";
import DataTable from "./DataTable.vue";
import Button from "./Button.vue";
import Modal from "./Modal.vue";
import { useToaster } from "ram-components";
import services from "@/services";
import { useAuthStore } from "@/stores/authStore";


const toaster = useToaster();
const authStore = useAuthStore();

type ModalMode = "create" | "edit";

const ranges = ref<any[]>([]);
const showModal = ref(false);
const modalMode = ref<ModalMode>("create");
const loading = ref(false);
const saving = ref(false);

const selectedId = ref(0);
const rangePrefix = ref("");
const isActive = ref(true);

const columns = [
  { key: "rangePrefix", label: "Range Prefix" },
  { key: "status", label: "Status" },
  { key: "action", label: "Action" },
];

onMounted(() => {
  fetchParcelRanges();
});

async function fetchParcelRanges() {
  loading.value = true;
  try {
    const response = await services.getParcelRangeDetails();
    const result = response.data.result || [];

    ranges.value = result.map((item: any) => ({
      recordID: item.recordID,
      rangePrefix: item.rangePrefix,
      status: item.status,
    }));
  } catch (error) {
    toaster.push({ type: "error", text: "Failed to load Parcel Ranges" });
  } finally {
    loading.value = false;
  }
}

function openCreateModal() {
  modalMode.value = "create";
  selectedId.value = 0;
  rangePrefix.value = "";
  isActive.value = true;

  showModal.value = true;
}

async function openEditModal(row: any) {
  modalMode.value = "edit";
  selectedId.value = row.recordID;

  try {
    const response = await services.getParcelRangeById(row.recordID);
    const details = response.data.result;

    if (details) {
      rangePrefix.value = details.consignmentID;
      isActive.value = details.isActive;
      showModal.value = true;
    }
  } catch (error) {
    toaster.push({ type: "error", text: "Failed to fetch details" });
  } finally {
  }
}
async function handleSave() {
  saving.value = true;

  const payload = {
    recordID: modalMode.value === "create" ? 0 : selectedId.value,
    consignmentID: rangePrefix.value,
    isActive: isActive.value,
    createdBy: authStore.currentUserID
  };

  try {
    if (modalMode.value === "create") {
      await services.saveParcelRange(payload);
      toaster.push({ type: "success", text: "Created successfully" });
    } else {
      await services.saveParcelRange(payload);
      toaster.push({ type: "success", text: "Updated successfully" });
    }

    showModal.value = false;
    await fetchParcelRanges();
  } catch (error: any) {
    const apiData = error.response?.data;
    if (apiData?.message === "Consignment Range already exists") {
      showModal.value = false;
      toaster.push({
        type: "warning",
        text: "Consignment Range already exists",
      });
    } else {
      toaster.push({
        type: "error",
        text: apiData?.message || "Failed to save details",
      });
    }
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <div class="rounded-xl border bg-white">
    <div class="flex items-center justify-between border-b p-4">
      <h2 class="font-semibold text-gray-900">Valid Parcel Numbers</h2>
      <button
        @click="openCreateModal"
        class="rounded-lg bg-ram-red px-4 py-2 text-sm text-white"
      >
        Add Range
      </button>
    </div>
    <DataTable
      :columns="columns"
      :data="ranges"
      :loading="loading"
      empty-message="No Ranges Added"
    >
      <template #cell-status="{ row }">
        <span
          :class="[
            'rounded-full px-2 py-1 text-xs font-medium',

            row.status === 'Active'
              ? 'bg-green-100 text-green-700'
              : 'bg-red-100 text-red-700',
          ]"
        >
          {{ row.status }}
        </span>
      </template>

      <template #cell-action="{ row }">
        <button
          class="text-sm text-blue-600 hover:underline"
          @click="openEditModal(row)"
        >
          Edit
        </button>
      </template>
    </DataTable>
  </div>

  <Modal
    :is-open="showModal"
    :title="modalMode === 'create' ? 'Add Range' : 'Edit Range'"
    size="lg"
    @close="showModal = false"
  >
    <div class="space-y-4">
      <FormKit
        v-model="rangePrefix"
        label="Parcel Range Prefix"
        validation="required| length:0,30"
        v-limit="30"
        validation-visibility="live"
        type="text"
        input-class="w-full px-2 py-1 my-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sbsa-blue"
        label-class="text-sm font-medium text-gray-700"
        :classes="{
          messages: 'text-red-600 text-xs mt-1',
          inputInvalid: 'border-red-500 focus:ring-red-500',
        }"
      />

      <ul class="style-none mx-3 text-xs text-gray-500">
        <li>Options :</li>
        <li># = Any digit</li>
        <li>A = Any letter</li>
        <li>N = Any capital alphanumeric character</li>
      </ul>

      <FormKit
        type="checkbox"
        v-if="modalMode === 'edit'"
        v-model="isActive"
        label="Active"
        :classes="{
          input:
            'h-4 w-4 mr-2 rounded border-gray-900 text-ram-red focus:ring-ram-red',
          label: 'text-sm font-medium text-gray-700 cursor-pointer',
        }"
      />
    </div>

    <template #footer>
      <div class="mt-3 flex justify-end gap-3">
        <Button variant="secondary" @click="showModal = false"> Cancel </Button>
        <Button
          variant="ram"
          @click="handleSave"
          :disabled="!rangePrefix || saving || rangePrefix.length > 30"
        >
          {{ saving ? "Saving..." : "Save" }}
        </Button>
      </div>
    </template>
  </Modal>
</template>
